import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, Progress, Table, Tag, Space, Spin, Divider, Button, Modal, Form, Input, InputNumber, Select, DatePicker, message, Alert, Badge, Collapse, Timeline } from 'antd';
import { DashboardOutlined, CloudServerOutlined, HddOutlined, WifiOutlined, ClockCircleOutlined, ThunderboltOutlined, ApiOutlined, DollarOutlined, EditOutlined, CreditCardOutlined, CalendarOutlined, WarningOutlined, CheckCircleOutlined, ExclamationCircleOutlined, BugOutlined, SafetyCertificateOutlined, HistoryOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export default function ServerMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  const [billing, setBilling] = useState(null);
  const [billingModal, setBillingModal] = useState(false);
  const [billingForm] = Form.useForm();
  const [savingBilling, setSavingBilling] = useState(false);
  const [health, setHealth] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.get('/performance/server-monitor');
      setData(res.data);
      setLastUpdate(new Date());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchBilling = async () => {
    try {
      const res = await api.get('/performance/server-billing');
      setBilling(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchHealth = async () => {
    try {
      const res = await api.get('/performance/server-health');
      setHealth(res.data);
    } catch (err) { console.error(err); }
  };

  const saveBilling = async (values) => {
    setSavingBilling(true);
    try {
      await api.post('/performance/server-billing', {
        ...values,
        next_payment_date: values.next_payment_date ? values.next_payment_date.format('YYYY-MM-DD') : '',
      });
      message.success('Billing info updated!');
      setBillingModal(false);
      fetchBilling();
    } catch (err) { message.error('Failed to save'); }
    setSavingBilling(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    fetchBilling();
    fetchHealth();
    intervalRef.current = setInterval(() => { fetchData(); fetchHealth(); }, 15000);
    const billingInterval = setInterval(fetchBilling, 30000);
    return () => { clearInterval(intervalRef.current); clearInterval(billingInterval); };
  }, []);

  const getColor = (pct) => pct >= 90 ? '#ff4d4f' : pct >= 75 ? '#faad14' : '#52c41a';
  const getStatus = (pct) => pct >= 90 ? 'Critical' : pct >= 75 ? 'Warning' : 'Healthy';
  const getStatusColor = (pct) => pct >= 90 ? 'red' : pct >= 75 ? 'orange' : 'green';

  if (loading && !data) return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}><DashboardOutlined /> Server Monitor</Title>
          <Text type="secondary">
            Live server performance — auto-refreshes every 15 seconds
            {lastUpdate && <span style={{ marginLeft: 12 }}>Last: {lastUpdate.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>}
          </Text>
        </Col>
        <Col>
          <Tag color={data ? 'green' : 'red'} style={{ fontSize: 14, padding: '4px 16px' }}>
            <span style={{ animation: 'pulse 2s infinite' }}>{data ? '● LIVE' : '● OFFLINE'}</span>
          </Tag>
        </Col>
      </Row>

      {/* Server Health Issues */}
      {health && health.total_issues > 0 && (
        <Card size="small" style={{ borderRadius: 12, marginBottom: 16, borderLeft: `4px solid ${health.status === 'critical' ? '#ff4d4f' : '#faad14'}` }}>
          <Row align="middle" style={{ marginBottom: 8 }}>
            <Col flex="auto">
              <Space>
                {health.status === 'critical' ? <ExclamationCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} /> : <WarningOutlined style={{ fontSize: 20, color: '#faad14' }} />}
                <Text strong style={{ fontSize: 16 }}>
                  Server Issues Detected
                </Text>
                {health.critical_count > 0 && <Tag color="red">{health.critical_count} Critical</Tag>}
                {health.warning_count > 0 && <Tag color="orange">{health.warning_count} Warning</Tag>}
              </Space>
            </Col>
            <Col><Text type="secondary" style={{ fontSize: 12 }}>Checked: {health.checked_at}</Text></Col>
          </Row>
          {health.issues.map((issue, idx) => (
            <Alert key={idx} type={issue.level === 'critical' ? 'error' : 'warning'} showIcon
              style={{ marginBottom: 6, borderRadius: 8 }}
              message={<Text strong>{issue.category}</Text>}
              description={<div>
                <div>{issue.message}</div>
                {issue.value && <Text code style={{ fontSize: 11, marginTop: 4, display: 'block', whiteSpace: 'pre-wrap' }}>{issue.value}</Text>}
                {issue.solution && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#f0f5ff', borderLeft: '3px solid #2f54eb', borderRadius: 4 }}>
                    <Text strong style={{ fontSize: 12, color: '#2f54eb' }}>✅ Solution — kya karein:</Text>
                    <div style={{ fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{issue.solution}</div>
                  </div>
                )}
              </div>} />

          ))}
        </Card>
      )}

      {health && health.total_issues === 0 && (
        <Alert type="success" showIcon icon={<CheckCircleOutlined />} style={{ marginBottom: 16, borderRadius: 8 }}
          message={<Text strong>All Systems Healthy</Text>}
          description={`No issues detected — Disk, RAM, CPU, Web Server, Database all OK. Services: Apache (${health.services?.apache}), MySQL (${health.services?.mysql}). ${health.cron_count} cron jobs active.`} />
      )}

      {data && (
        <>
          {/* Main Stats Row */}
          <Row gutter={[16, 16]}>
            {/* CPU */}
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: 12, borderTop: `3px solid ${getColor(data.cpu_percent)}` }}>
                <div style={{ textAlign: 'center' }}>
                  <Progress type="dashboard" percent={Math.min(data.cpu_percent, 100)} strokeColor={getColor(data.cpu_percent)} size={120}
                    format={p => <span style={{ fontSize: 22, fontWeight: 'bold' }}>{p}%</span>} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ fontSize: 16 }}>CPU Usage</Text>
                  </div>
                  <Tag color={getStatusColor(data.cpu_percent)} style={{ marginTop: 4 }}>{getStatus(data.cpu_percent)}</Tag>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{data.cpu_cores} Cores | Load: {data.cpu_load_1m}</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* RAM */}
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: 12, borderTop: `3px solid ${getColor(data.ram_percent)}` }}>
                <div style={{ textAlign: 'center' }}>
                  <Progress type="dashboard" percent={data.ram_percent} strokeColor={getColor(data.ram_percent)} size={120}
                    format={p => <span style={{ fontSize: 22, fontWeight: 'bold' }}>{p}%</span>} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ fontSize: 16 }}>RAM Usage</Text>
                  </div>
                  <Tag color={getStatusColor(data.ram_percent)} style={{ marginTop: 4 }}>{getStatus(data.ram_percent)}</Tag>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{data.ram_used_gb} / {data.ram_total_gb} GB</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Disk */}
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: 12, borderTop: `3px solid ${getColor(data.disk_percent)}` }}>
                <div style={{ textAlign: 'center' }}>
                  <Progress type="dashboard" percent={data.disk_percent} strokeColor={getColor(data.disk_percent)} size={120}
                    format={p => <span style={{ fontSize: 22, fontWeight: 'bold' }}>{p}%</span>} />
                  <div style={{ marginTop: 8 }}>
                    <Text strong style={{ fontSize: 16 }}>Disk Usage</Text>
                  </div>
                  <Tag color={getStatusColor(data.disk_percent)} style={{ marginTop: 4 }}>{getStatus(data.disk_percent)}</Tag>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{data.disk_used_gb} / {data.disk_total_gb} GB</Text>
                  </div>
                </div>
              </Card>
            </Col>

            {/* Uptime */}
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: 12, borderTop: '3px solid #667eea' }}>
                <div style={{ textAlign: 'center' }}>
                  <ClockCircleOutlined style={{ fontSize: 48, color: '#667eea', marginBottom: 12 }} />
                  <div><Text strong style={{ fontSize: 16 }}>Uptime</Text></div>
                  <div style={{ marginTop: 8 }}>
                    <Text style={{ fontSize: 14 }}>{data.uptime || 'N/A'}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Since: {data.uptime_since || 'N/A'}</Text>
                  </div>
                  <Tag color="blue" style={{ marginTop: 8 }}>{data.server_time}</Tag>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Detailed Stats */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {/* CPU Details */}
            <Col xs={24} md={8}>
              <Card size="small" title={<span><ThunderboltOutlined style={{ color: '#faad14' }} /> CPU Details</span>} style={{ borderRadius: 12 }}>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Load (1 min)</Text></Col><Col span={10}><Text strong>{data.cpu_load_1m}</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Load (5 min)</Text></Col><Col span={10}><Text strong>{data.cpu_load_5m}</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Load (15 min)</Text></Col><Col span={10}><Text strong>{data.cpu_load_15m}</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">CPU Cores</Text></Col><Col span={10}><Text strong>{data.cpu_cores}</Text></Col></Row>
                <Row><Col span={14}><Text type="secondary">Usage %</Text></Col><Col span={10}><Text strong style={{ color: getColor(data.cpu_percent) }}>{data.cpu_percent}%</Text></Col></Row>
              </Card>
            </Col>

            {/* RAM Details */}
            <Col xs={24} md={8}>
              <Card size="small" title={<span><CloudServerOutlined style={{ color: '#1890ff' }} /> Memory Details</span>} style={{ borderRadius: 12 }}>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Total RAM</Text></Col><Col span={10}><Text strong>{data.ram_total_gb} GB</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Used RAM</Text></Col><Col span={10}><Text strong style={{ color: getColor(data.ram_percent) }}>{data.ram_used_gb} GB</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Free RAM</Text></Col><Col span={10}><Text strong style={{ color: '#52c41a' }}>{data.ram_free_gb} GB</Text></Col></Row>
                <Divider style={{ margin: '8px 0' }} />
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Swap Total</Text></Col><Col span={10}><Text strong>{data.swap_total_gb} GB</Text></Col></Row>
                <Row><Col span={14}><Text type="secondary">Swap Used</Text></Col><Col span={10}><Text strong>{data.swap_used_gb} GB</Text></Col></Row>
              </Card>
            </Col>

            {/* Disk Details */}
            <Col xs={24} md={8}>
              <Card size="small" title={<span><HddOutlined style={{ color: '#722ed1' }} /> Disk Details</span>} style={{ borderRadius: 12 }}>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Total Disk</Text></Col><Col span={10}><Text strong>{data.disk_total_gb} GB</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Used Disk</Text></Col><Col span={10}><Text strong style={{ color: getColor(data.disk_percent) }}>{data.disk_used_gb} GB</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={14}><Text type="secondary">Free Disk</Text></Col><Col span={10}><Text strong style={{ color: '#52c41a' }}>{data.disk_free_gb} GB</Text></Col></Row>
                <Divider style={{ margin: '8px 0' }} />
                <Progress percent={data.disk_percent} strokeColor={getColor(data.disk_percent)} />
              </Card>
            </Col>
          </Row>

          {/* Network + Server Info */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card size="small" title={<span><WifiOutlined style={{ color: '#13c2c2' }} /> Network</span>} style={{ borderRadius: 12 }}>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Hostname</Text></Col><Col span={12}><Text strong>{data.hostname}</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Private IP</Text></Col><Col span={12}><Text strong>{data.ip_private}</Text></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Public IP</Text></Col><Col span={12}><Text strong>{data.ip_public}</Text></Col></Row>
                {data.network && data.network.interface && <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Interface</Text></Col><Col span={12}><Text strong>{data.network.interface}</Text></Col></Row>
                  <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Data Received</Text></Col><Col span={12}><Text strong style={{ color: '#52c41a' }}>{data.network.rx_gb} GB</Text></Col></Row>
                  <Row><Col span={12}><Text type="secondary">Data Sent</Text></Col><Col span={12}><Text strong style={{ color: '#1890ff' }}>{data.network.tx_gb} GB</Text></Col></Row>
                </>}
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card size="small" title={<span><ApiOutlined style={{ color: '#eb2f96' }} /> Server Info</span>} style={{ borderRadius: 12 }}>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">PHP Version</Text></Col><Col span={12}><Tag color="blue">{data.php_version}</Tag></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">MySQL Version</Text></Col><Col span={12}><Tag color="green">{data.mysql_version}</Tag></Col></Row>
                <Row style={{ marginBottom: 8 }}><Col span={12}><Text type="secondary">Server Time</Text></Col><Col span={12}><Text strong>{data.server_time}</Text></Col></Row>
                {data.active_connections && <Row><Col span={12}><Text type="secondary">Connections</Text></Col><Col span={12}><Text style={{ fontSize: 12 }}>{data.active_connections}</Text></Col></Row>}
              </Card>
            </Col>
          </Row>

          {/* Server Billing & Payment */}
          {billing && (
            <Card size="small" style={{ borderRadius: 12, marginTop: 16, background: 'linear-gradient(135deg, #667eea11, #764ba211)' }}
              title={<Space><DollarOutlined style={{ color: '#52c41a' }} /> <span>Server Billing & Payment</span>
                {billing.live_data && <Tag color="green" style={{ fontSize: 11 }}>LIVE from DO API</Tag>}
                {billing.api_connected && !billing.live_data && <Tag color="orange" style={{ fontSize: 11 }}>API connected (fetching...)</Tag>}
                {!billing.api_connected && <Tag color="default" style={{ fontSize: 11 }}>Manual mode</Tag>}
              </Space>}
              extra={<Button size="small" icon={<EditOutlined />} onClick={() => { billingForm.setFieldsValue({ ...billing, next_payment_date: billing.next_payment_date ? dayjs(billing.next_payment_date) : null }); setBillingModal(true); }}>Edit</Button>}>
              {(billing.plan_name || billing.live_data) ? (
                <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ borderRadius: 10, textAlign: 'center', borderTop: '3px solid #667eea' }}>
                      <CreditCardOutlined style={{ fontSize: 28, color: '#667eea', marginBottom: 8 }} />
                      <div><Text type="secondary">Current Plan</Text></div>
                      <div style={{ fontSize: 16, fontWeight: 'bold', color: '#667eea' }}>{billing.plan_name || 'N/A'}</div>
                      <div style={{ fontSize: 22, fontWeight: 'bold', marginTop: 4 }}>${billing.plan_price}<Text type="secondary" style={{ fontSize: 13 }}>/{billing.billing_cycle === 'yearly' ? 'yr' : 'mo'}</Text></div>
                      <Tag color="blue" style={{ marginTop: 4 }}>{billing.provider || 'InterServer VPS'}</Tag>
                      {(billing.vps_status || billing.droplet_status) && <div style={{ marginTop: 4 }}><Tag color={(billing.vps_status || billing.droplet_status) === 'online' || (billing.vps_status || billing.droplet_status) === 'active' ? 'green' : 'red'}>{billing.vps_status || billing.droplet_status}</Tag></div>}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ borderRadius: 10, textAlign: 'center', borderTop: `3px solid ${billing.account_balance <= billing.plan_price ? '#ff4d4f' : billing.account_balance <= billing.plan_price * 2 ? '#faad14' : '#52c41a'}` }}>
                      <DollarOutlined style={{ fontSize: 28, color: billing.account_balance <= billing.plan_price ? '#ff4d4f' : '#52c41a', marginBottom: 8 }} />
                      <div><Text type="secondary">Available Credits</Text></div>
                      <div style={{ fontSize: 26, fontWeight: 'bold', color: billing.account_balance <= billing.plan_price ? '#ff4d4f' : '#52c41a' }}>${billing.account_balance}</div>
                      {billing.plan_price > 0 && (() => {
                        const months = Math.floor(billing.account_balance / billing.plan_price);
                        return <Tag color={months <= 1 ? 'red' : months <= 2 ? 'orange' : 'green'} style={{ marginTop: 4 }}>~{months} month{months !== 1 ? 's' : ''} remaining</Tag>;
                      })()}
                      {billing.month_to_date_usage > 0 && <div style={{ marginTop: 6 }}><Text type="secondary" style={{ fontSize: 11 }}>This month used: ${billing.month_to_date_usage.toFixed(2)}</Text></div>}
                      {billing.credit_source && <div style={{ marginTop: 4 }}><Tag color="purple" style={{ fontSize: 10 }}>{billing.credit_source}</Tag></div>}
                      {billing.credit_expiry && <div><Text type="secondary" style={{ fontSize: 10 }}>Expires: {billing.credit_expiry}</Text></div>}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ borderRadius: 10, textAlign: 'center', borderTop: '3px solid #faad14' }}>
                      <CalendarOutlined style={{ fontSize: 28, color: '#faad14', marginBottom: 8 }} />
                      <div><Text type="secondary">Next Payment</Text></div>
                      <div style={{ fontSize: 18, fontWeight: 'bold' }}>{billing.next_payment_date ? dayjs(billing.next_payment_date).format('DD MMM YYYY') : 'Not set'}</div>
                      <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa541c', marginTop: 4 }}>${billing.plan_price}</div>
                      {billing.next_payment_date && <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(billing.next_payment_date).diff(dayjs(), 'day')} days away</Text>}
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small" style={{ borderRadius: 10, textAlign: 'center', borderTop: '3px solid #13c2c2' }}>
                      <CloudServerOutlined style={{ fontSize: 28, color: '#13c2c2', marginBottom: 8 }} />
                      <div><Text type="secondary">After Next Payment</Text></div>
                      {billing.plan_price > 0 && (() => {
                        const afterPay = billing.account_balance - billing.plan_price;
                        const monthsAfter = Math.floor(afterPay / billing.plan_price);
                        return <>
                          <div style={{ fontSize: 22, fontWeight: 'bold', color: afterPay < 0 ? '#ff4d4f' : '#52c41a' }}>${afterPay.toFixed(0)}</div>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>Balance remaining</Text></div>
                          {afterPay < billing.plan_price ? (
                            <Tag color="red" style={{ marginTop: 4 }}>Payment needed!</Tag>
                          ) : (
                            <Tag color="green" style={{ marginTop: 4 }}>~{monthsAfter} more month{monthsAfter !== 1 ? 's' : ''}</Tag>
                          )}
                        </>;
                      })()}
                    </Card>
                  </Col>
                </Row>
                {/* Droplet Details */}
                <div style={{ marginTop: 12 }}>
                  <Space size="large" wrap>
                    {billing.droplet_name && <Text type="secondary">Server: <Text strong>{billing.droplet_name}</Text></Text>}
                    {billing.droplet_region && <Text type="secondary">Region: <Tag>{billing.droplet_region.toUpperCase()}</Tag></Text>}
                    {billing.droplet_id && <Text type="secondary">ID: <Text code>{billing.droplet_id}</Text></Text>}
                    {billing.droplet_vcpus && <Text type="secondary">vCPUs: <Text strong>{billing.droplet_vcpus}</Text></Text>}
                    {billing.droplet_memory_mb && <Text type="secondary">RAM: <Text strong>{(billing.droplet_memory_mb/1024).toFixed(0)} GB</Text></Text>}
                    {billing.droplet_disk_gb && <Text type="secondary">Disk: <Text strong>{billing.droplet_disk_gb} GB</Text></Text>}
                    {billing.droplet_image && <Text type="secondary">OS: <Tag>{billing.droplet_image}</Tag></Text>}
                    {billing.payment_method && <Text type="secondary">Payment: <Text strong>{billing.payment_method}</Text></Text>}
                  </Space>
                </div>
                {billing.bandwidth_total_gb > 0 && (
                  <Card size="small" style={{ borderRadius: 10, marginTop: 12, borderLeft: '4px solid #722ed1' }}>
                    <Row align="middle" gutter={16}>
                      <Col flex="none"><WifiOutlined style={{ fontSize: 26, color: '#722ed1' }} /></Col>
                      <Col flex="auto">
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                          <Text strong>Bandwidth (this month){billing.bandwidth_source === 'server' ? <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>MEASURED ON THIS SERVER</Tag> : billing.vps_api_connected && <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>LIVE from VPS panel</Tag>}</Text>
                          <Text type="secondary">{billing.bandwidth_used_gb} GB used of {billing.bandwidth_total_gb} GB</Text>
                        </div>
                        <Progress percent={Math.min(billing.bandwidth_percent, 100)} strokeColor={billing.bandwidth_percent >= 90 ? '#ff4d4f' : billing.bandwidth_percent >= 70 ? '#faad14' : '#722ed1'} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{billing.bandwidth_free_gb} GB remaining</Text>
                      </Col>
                    </Row>
                  </Card>
                )}
                {billing.notes && <Card size="small" style={{ borderRadius: 8, background: '#fffbe6', marginTop: 8 }}><Text><strong>Notes:</strong> {billing.notes}</Text></Card>}
                {/* Billing History from DO API */}
                {billing.billing_history && billing.billing_history.length > 0 && (
                  <Card size="small" style={{ borderRadius: 8, marginTop: 12 }} title={<span><HistoryOutlined /> Recent Billing History</span>}>
                    <Table dataSource={billing.billing_history} rowKey={(r,i) => `bh-${i}`} size="small" pagination={false}
                      columns={[
                        { title: 'Date', dataIndex: 'date', width: 140, render: v => v ? dayjs(v).format('DD MMM YYYY') : '' },
                        { title: 'Type', dataIndex: 'type', width: 100, render: v => <Tag color={v === 'Invoice' ? 'blue' : v === 'Payment' ? 'green' : 'default'}>{v}</Tag> },
                        { title: 'Description', dataIndex: 'description', ellipsis: true },
                        { title: 'Amount', dataIndex: 'amount', width: 100, render: v => <Text strong>${v}</Text> },
                      ]} />
                  </Card>
                )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <DollarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 12 }} />
                  <div><Text type="secondary">No billing info set yet</Text></div>
                  <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Add billing details manually or connect DigitalOcean API for live data</Text></div>
                  <Button type="primary" icon={<EditOutlined />} style={{ marginTop: 12 }} onClick={() => { billingForm.resetFields(); setBillingModal(true); }}>Add Billing Info</Button>
                </div>
              )}
            </Card>
          )}

          {/* Top Processes */}
          {data.top_processes && data.top_processes.length > 0 && (
            <Card size="small" title={<span><ThunderboltOutlined style={{ color: '#fa541c' }} /> Top Processes (by CPU)</span>} style={{ borderRadius: 12, marginTop: 16 }}>
              <Table
                dataSource={data.top_processes}
                rowKey={(r, i) => `${r.pid}-${i}`}
                size="small"
                pagination={false}
                columns={[
                  { title: 'PID', dataIndex: 'pid', width: 70 },
                  { title: 'User', dataIndex: 'user', width: 80 },
                  { title: 'CPU %', dataIndex: 'cpu', width: 80, render: v => <Text strong style={{ color: v > 50 ? '#ff4d4f' : v > 20 ? '#faad14' : '#52c41a' }}>{v}%</Text> },
                  { title: 'MEM %', dataIndex: 'mem', width: 80, render: v => <Text>{v}%</Text> },
                  { title: 'Command', dataIndex: 'command', ellipsis: true, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
                ]}
              />
            </Card>
          )}
        </>
      )}

      {/* Billing Edit Modal */}
      <Modal title="Server Billing Info" open={billingModal} onCancel={() => setBillingModal(false)} footer={null} width={520}>
        <Form form={billingForm} layout="vertical" onFinish={saveBilling}>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="provider" label="Provider" initialValue="DigitalOcean">
                <Input placeholder="DigitalOcean" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="plan_name" label="Plan Name">
                <Input placeholder="e.g. Basic Droplet 8GB" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="plan_price" label="Plan Price ($)">
                <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="48" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="billing_cycle" label="Billing Cycle" initialValue="monthly">
                <Select options={[{ value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="account_balance" label="Available Credits ($)" extra="Copy from DO billing page">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="111.96" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="next_payment_date" label="Next Payment Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="payment_method" label="Payment Method">
                <Input placeholder="e.g. Visa *4242" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Any notes about billing..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="credit_source" label="Credit Source">
                <Input placeholder="e.g. GitHub Student Pack" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="credit_expiry" label="Credit Expiry Date">
                <Input placeholder="e.g. August 01, 2026" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="bandwidth_total_gb" label="Monthly Bandwidth Quota (GB)" extra="Total transfer allowed per month per your VPS plan. Usage is measured live on this server.">
            <InputNumber min={0} step={100} style={{ width: '100%' }} placeholder="3000" />
          </Form.Item>
          <Divider style={{ margin: '12px 0' }}><Text type="secondary" style={{ fontSize: 12 }}>DigitalOcean API (Optional — for live data)</Text></Divider>
          <Form.Item name="do_api_token" label={<span><LinkOutlined /> DO API Token</span>} extra="Add token for live balance, plan details, billing history. Get from cloud.digitalocean.com/account/api/tokens">
            <Input.Password placeholder="dop_v1_xxxxx..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={savingBilling} block>Save Billing Info</Button>
        </Form>
      </Modal>

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
}
